import { useState, type FormEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { createSupportTicket, type SupportCategory } from '../../api';
import { APP_VERSION } from '../../appVersion';
import { useAircraft } from '../../aircraft/AircraftContext';
import { useAuth } from '../../auth/AuthContext';
import { useFeedback } from '../../hooks/useFeedback';
import { useSubmitGuard } from '../../hooks/useSubmitGuard';
import { notifySupportUpdated } from '../../hooks/useSupportUnread';
import { getApiErrorMessage } from '../../utils/apiError';
import {
  BODY_MAX,
  SUBJECT_MAX,
  SUPPORT_CATEGORIES,
  screenLabel,
} from './supportLabels';
import './SupportPage.css';

const SupportNewPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { current } = useAircraft();
  const { showError, feedbackNode } = useFeedback();
  const { submitting, runSubmit } = useSubmitGuard();

  const [category, setCategory] = useState<SupportCategory>('other');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');

  const from = (location.state as { from?: string } | null)?.from;
  const screen = screenLabel(from);
  const aircraftTail = current?.tail_number || '';

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    void runSubmit(async () => {
      try {
        const ticket = await createSupportTicket({
          category,
          subject: subject.trim(),
          body: body.trim(),
          platform: 'web',
          app_version: APP_VERSION,
          screen,
          ...(aircraftTail ? { aircraft_tail: aircraftTail } : {}),
        });
        notifySupportUpdated();
        navigate(`/support/${ticket.id}`, { replace: true });
      } catch (err) {
        showError(getApiErrorMessage(err, 'Не удалось отправить обращение'));
      }
    });
  };

  const canSubmit = subject.trim() !== '' && body.trim() !== '' && !submitting;

  return (
    <div className="support-page">
      {feedbackNode}
      <div className="support-container">
        <Link to="/support" className="support-back">
          ← Мои обращения
        </Link>
        <header className="support-header">
          <h2>Новое обращение</h2>
        </header>

        <form className="support-form ui-panel" onSubmit={handleSubmit}>
          <div className="support-field">
            <label htmlFor="support-category">Тема</label>
            <select
              id="support-category"
              value={category}
              onChange={(e) => setCategory(e.target.value as SupportCategory)}
            >
              {SUPPORT_CATEGORIES.map((item) => (
                <option key={item.value} value={item.value}>
                  {item.label}
                </option>
              ))}
            </select>
          </div>

          <div className="support-field">
            <label htmlFor="support-subject">Заголовок</label>
            <input
              id="support-subject"
              value={subject}
              maxLength={SUBJECT_MAX}
              required
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Коротко: что случилось"
            />
          </div>

          <div className="support-field">
            <label htmlFor="support-body">Описание</label>
            <textarea
              id="support-body"
              value={body}
              maxLength={BODY_MAX}
              required
              onChange={(e) => setBody(e.target.value)}
              placeholder="Что вы делали, что ожидали увидеть и что произошло"
            />
            <span className="support-counter">
              {body.length} / {BODY_MAX}
            </span>
          </div>

          <section className="support-context" aria-labelledby="support-context-title">
            <p id="support-context-title" className="support-context-title">
              Вместе с обращением отправится
            </p>
            <dl>
              <dt>Платформа</dt>
              <dd>Web, версия {APP_VERSION}</dd>
              <dt>Экран</dt>
              <dd>{screen}</dd>
              {user?.club_name ? (
                <>
                  <dt>Клуб</dt>
                  <dd>{user.club_name}</dd>
                </>
              ) : null}
              {aircraftTail ? (
                <>
                  <dt>Борт</dt>
                  <dd>{aircraftTail}</dd>
                </>
              ) : null}
            </dl>
          </section>

          <div className="support-actions">
            <Link to="/support" className="support-btn">
              Отмена
            </Link>
            <button
              type="submit"
              className="support-btn support-btn--primary"
              disabled={!canSubmit}
            >
              {submitting ? 'Отправка…' : 'Отправить'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SupportNewPage;
